/**
 * IndexedDB copy of the scheme reference, so the pickers have it immediately on start (and offline on native) while
 * `SchemeReferenceService` checks the server's hash in the background (tasks/scheme-linking-central.md).
 * Same pattern as `core/services/procedure-codebook-cache.ts`.
 *
 * ── NO CACHED HANDLE ─────────────────────────────────────────────────────────────────
 *
 * `tasks/lessons.md` §16: browsers force-close IndexedDB connections, and a cached handle stays
 * truthy while every transaction on it throws. This is read once per session and written only when
 * the server version moves, so every operation opens its own connection and closes it, and a
 * failed operation is retried once on a fresh one. Nothing here can hand out a dead handle.
 *
 * Failure is never fatal: a read that fails returns null (the app fetches from the server), and a
 * write that fails only costs the next cold start a download.
 */

import type { SchemeReferenceData } from './scheme-reference-loader';

export interface CachedSchemeReference {
  hash: string;
  data: SchemeReferenceData;
  savedAt: number;
}

const DB_NAME = 'medicai-scheme-reference';
const DB_VERSION = 1;
const STORE = 'codebook';
const KEY = 'current';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const attempt = async (): Promise<T> => {
    const db = await open();
    try {
      return await new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = run(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(request.result);
        tx.onerror = () => reject(tx.error || request.error);
        tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
      });
    } finally {
      db.close();
    }
  };
  try {
    return await attempt();
  } catch {
    return attempt(); // once, on a fresh connection
  }
}

export async function readCachedSchemeReference(): Promise<CachedSchemeReference | null> {
  try {
    const value = await withStore<CachedSchemeReference | undefined>('readonly', (store) => store.get(KEY));
    return value && typeof value.hash === 'string' && Array.isArray(value.data?.schemes) ? value : null;
  } catch (error) {
    console.warn('Scheme reference cache unreadable; will fetch from the server', error);
    return null;
  }
}

export async function writeCachedSchemeReference(hash: string, data: SchemeReferenceData): Promise<void> {
  try {
    await withStore('readwrite', (store) => store.put({ hash, data, savedAt: Date.now() } as CachedSchemeReference, KEY));
  } catch (error) {
    console.warn('Scheme reference cache not saved', error);
  }
}
